import React, { useEffect, useMemo, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { assignWith, cloneDeep, debounce, get } from 'lodash';
import { useQueries } from '@tanstack/react-query';
import { getPercentageMetricByKey } from '@utils/metrics/metricHelpers';
import { dispatchComparLoadingStateChangeEvent } from '@components/summary-panel/CompareFiltersBar/utils';
import { unitAtom } from '../atoms';
import instance from '@api/axiosInstance';
import getMetricsData from '@src/api/metricContainer';
import { trackQueryError } from '@utils/sensor/apptracker';
import { formatTimeseriesData } from '../utils/format';
import { findAxisIndex } from './utils';
import { TIME_AXIS, VALUE_AXIS } from '../utils/constants';
import useCompareTimeseriesQueries from './useCompareTimeseriesQueries';
import { buildCompareSeries } from '../utils/compareSeriesOption';
import { DATA_TAGS, useGetData, useSetData } from './useData';
import { concurrencyLimiter } from './concurrencyUtils';
import { compareOnTrendsFiltersMetaSelector } from '@src/store/trends/base/selectors';
import { useSelector } from 'react-redux';
import { addLocalPercentageMetrics } from '@src/utils/table/parseUtils';
import { processAnnotations } from '@src/components/common/DataAnnotations';

const defaultResponseValue = {
  flatData: [],
  flatTotals: [],
  formattedData: [],
  formattedTotals: [],
  // duplicated state as this is part of the data and will change in sync with it
  compareFilters: [],
};

const COMPARE_QUERY_CONCURRENCY_LIMIT = 3;

// wrapper to manage loading state
const compareRequestWrapper = (filter, params: any[]) => {
  dispatchComparLoadingStateChangeEvent({ enabled: true, filter });
  return getMetricsData(...params);
};
// rate limiter
const rateLimitedGetMetricsData = concurrencyLimiter(
  compareRequestWrapper,
  COMPARE_QUERY_CONCURRENCY_LIMIT,
);

// hook to fetch dod data
export function useFetchCompare({
  enabled = true,
  useBuildQuery = useCompareTimeseriesQueries,
  dataTag = DATA_TAGS.compare,
}) {
  const {
    queries = [],
    metrics = [],
    interval,
    compareFilters = [],
    realTimeOnTrends = false,
  }: any = useBuildQuery();

  const setCompareSeriesData = useSetData({ tag: dataTag, defaultValue: defaultResponseValue });

  // clear all pending queued filters
  useMemo(() => {
    if (compareFilters.length === 0) {
      rateLimitedGetMetricsData.clearQueue();
    }
  }, [compareFilters]);

  const resList = useQueries({
    queries: queries.map((query, idx) => {
      return {
        queryKey: ['compare-timeseries', query],
        queryFn: async () => {
          dispatchComparLoadingStateChangeEvent({ enabled: false, filter: compareFilters[idx] });
          const response = await rateLimitedGetMetricsData(compareFilters[idx], [
            { queries: [query] },
            {},
            instance,
          ]);
          const { data = [], totals = {}, annotations = [] } = get(response, ['data', 0], {});
          const parsedData = formatTimeseriesData({ metrics, data, interval, annotations });
          processAnnotations(annotations);
          return { parsedData, totals };
        },

        enabled: enabled && metrics.length > 0 && !realTimeOnTrends,
        refetchOnWindowFocus: false,

        // 1m
        staleTime: 1 * 60 * 1000,

        keepPreviousData: realTimeOnTrends,
        placeholderData: {},
        retry: 2,

        onError: (err) => {
          dispatchComparLoadingStateChangeEvent({
            error: true,
            filter: compareFilters[idx],
          });
          trackQueryError({
            type: 'compare-series',
            err,
          });
        },
      };
    }),
  });

  // slightly risky optimization, revert to resList in case of issues
  const memoKey = resList.reduce(
    (acc, res) => `${acc}-${res.dataUpdatedAt}-${res.errorUpdatedAt}`,
    '',
  );

  // convert data from metric values per filter => metric with per filter values
  const { data, isLoading } = useMemo(() => {
    let isLoading = false;

    const chartData: any = {};
    const chartTotals: any = {};
    metrics.forEach((m) => {
      chartData[m] = [];
      chartTotals[m] = [];
    });

    const formattedData: any[] = [];
    const formattedTotals: any[] = [];
    const loadingState: boolean[] = [];
    resList.forEach(({ isFetching, data = {} }: any) => {
      const { parsedData = cloneDeep(chartData), totals = cloneDeep(chartTotals) } = data;
      formattedData.push(parsedData);
      formattedTotals.push(totals);
      loadingState.push(isFetching);
      // overall indicator
      isLoading = isLoading || isFetching;
    });

    const flatten = (x, y) => {
      x.push(y);
      return x;
    };

    const flatData: {} = assignWith(chartData, ...formattedData, flatten);
    const flatTotals: {} = assignWith(chartTotals, ...formattedTotals, flatten);

    // total across all series
    // used only for calculating local percentage metrics
    const totalSummary = {};
    Object.keys(flatTotals).forEach((m) => {
      totalSummary[m] = flatTotals[m].reduce((t, v) => t + v, 0);
    });

    // clone before adding pct metrics to avoid impacting flatten loop above
    const formattedTotalsWithLocalPctMetrics = cloneDeep(formattedTotals);
    addLocalPercentageMetrics(metrics, formattedTotalsWithLocalPctMetrics, totalSummary);

    return {
      data: {
        flatData,
        flatTotals,
        formattedData,
        formattedTotals: formattedTotalsWithLocalPctMetrics,
        compareFilters,
        loadingState,
      },
      isLoading,
    };
  }, [metrics, memoKey]);

  useEffect(() => {
    setCompareSeriesData(data);
  }, [data, setCompareSeriesData]);

  return { isFetching: isLoading };
}

export const useCompareData = ({ metric = null, dataTag }) => {
  const {
    flatData,
    flatTotals,
    formattedData,
    formattedTotals,
    compareFilters,
    loadingState,
  }: any = useGetData({ tag: dataTag, defaultValue: defaultResponseValue });

  return {
    data: metric ? flatData[metric] : formattedData,
    totals: metric ? flatTotals[metric] : formattedTotals,
    compareFilters,
    loadingState,
  };
};

export function formatCompareSeries({
  option,
  data = [],
  compareFilters,
  barChart = false,
  compareFiltersMetaMap,
}) {
  const baseSeries = buildCompareSeries(data, {
    xAxisIndex: findAxisIndex(option.xAxis, TIME_AXIS),
    yAxisIndex: findAxisIndex(option.yAxis, VALUE_AXIS),
    compareFilters,
    barChart,
    compareFiltersMetaMap,
  });

  return { series: baseSeries };
}

function useCompareSeries({
  metric,
  instance,
  additionalTriggerDeps = [],
  dataTag = DATA_TAGS.compare,
  barChart = false,
  enabled = false,
}: any) {
  const unit = useAtomValue(unitAtom);
  const selectedMetric = unit === '%' ? getPercentageMetricByKey({ metricKey: metric }) : metric;
  let { data = [], compareFilters } = useCompareData({ metric, dataTag });

  // meta state changes do not reflect on `compareFilters` as part of data
  // to avoid index mismatches
  const compareFiltersMetaMap = useSelector(compareOnTrendsFiltersMetaSelector);

  // workaround to disable a series
  if (!enabled) {
    data = [];
    compareFilters = [];
  }

  const plotOnChart = useRef(
    debounce((instance, data, compareFilters, barChart, compareFiltersMetaMap) => {
      if (instance) {
        const option = instance.getOption();
        if (option && option._viewType === 'timeseries') {
          const { series } = formatCompareSeries({
            option,
            data,
            compareFilters,
            barChart,
            compareFiltersMetaMap,
          });

          instance.setOption({ series });
        }
      }
    }, 300),
  );

  useEffect(() => {
    plotOnChart.current(instance, data, compareFilters, barChart, compareFiltersMetaMap);
  }, [
    instance,
    selectedMetric,
    data,
    compareFilters,
    barChart,
    compareFiltersMetaMap,
    ...additionalTriggerDeps,
  ]);
}

export default useCompareSeries;
